import { Component, OnInit, Input, Output, EventEmitter, ViewChild } from '@angular/core';
import { AmorphicService } from '../../../../services/amorphic/amorphic.service';
import { GeoChangeService } from '../../../../services/geoChange/geoChange.service';
import { Subscription } from 'rxjs';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ValidatorService } from '../../../../../../common/angular-shared/services/validators-reactive.service';
import { FormatterService } from '../../../../services/formatter/formatter.service';
import { distinctUntilChanged } from 'rxjs/operators';
import { BasicTextInputComponent } from '../../../form/base-components/base-text-input/basic-text-input.component';

@Component({
  moduleId: module.id,
  selector: 'ssn-correction-form',
  templateUrl: './ssn-form.component.html',
})
export class SSNFormComponent implements OnInit {
  @Input() personType: string;
  @Output() toggleLoadingSpinner: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() submitCorrection: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() showLegalInfoPage: EventEmitter<void> = new EventEmitter<void>();
  @Output() zipChanged: EventEmitter<FormGroup> = new EventEmitter<FormGroup>();
  @ViewChild('ssnValue', { static: false }) ssnInput: BasicTextInputComponent;
  obfuscationPattern: string = '••• - •• - ••••';

  zipChangeSubscription: Subscription;
  ssnForm: FormGroup;
  changesWereMade: boolean = false;
  person;
  controller;
  headerText: string = 'What is your information?';
  oldSsnValue: string;
  personString: string;

  constructor(
    private formBuilder: FormBuilder,
    private amorphicService: AmorphicService,
    private geoChangeService: GeoChangeService,
    private formatterService: FormatterService
  ) {
    this.controller = amorphicService.controller;

    this.ssnForm = this.formBuilder.group({
      firstName: ['', Validators.required],
      middleName: [''],
      lastName: ['', Validators.required],
      ssn: ['', [ValidatorService.ssnValidator, Validators.required]],
      dob: ['', [ValidatorService.pastDateValidator, Validators.required]],
      street: ['', Validators.required],
      line1: [''],
      city: ['', Validators.required],
      state: ['', Validators.required],
      zip: ['', [Validators.required, ValidatorService.zipValidator]],
      oldState: [''],
      oldZip: [''],
    });
  }

  async ngOnInit() {
    this.setPerson();
    await this.initializeSsnForm();

    this.ssnForm.valueChanges.subscribe(() => {
      this.checkForChanges();
    });

    this.ssnForm
      .get('zip')
      .valueChanges.pipe(distinctUntilChanged())
      .subscribe(() => {
        this.zipChanged.emit(this.ssnForm);
      });

    this.zipChangeSubscription = this.geoChangeService.geoChangeSource$.subscribe((event) => {
      this.handleGeoChange(event);
    });

    this.ssnInput.val = this.ssnForm.get('ssn').invalid ? this.ssnForm.get('ssn').value : this.obfuscationPattern;
  }

  private async initializeSsnForm(): Promise<void> {
    const date = this.formatterService.formatUTCDate(this.person.dob as Date);
    this.oldSsnValue = await this.controller.getPiiValueByKey(this.person.SSN);

    this.ssnForm.patchValue({
      firstName: this.person.firstName,
      middleName: this.person.middleName,
      lastName: this.person.lastName,
      ssn: this.oldSsnValue,
      dob: date,
      street: this.person.residentialAddress.street,
      line1: this.person.residentialAddress.line1,
      city: this.person.residentialAddress.city,
      state: this.person.residentialAddress.state,
      zip: this.person.residentialAddress.zip,
      oldState: this.person.residentialAddress.state,
      oldZip: this.person.residentialAddress.zip,
    });
  }

  private setPerson(): void {
    if (this.personType === 'owner') {
      this.personString = 'ownerPerson';
      this.person = this.controller.customer.application.primaryPolicy.owner;
      this.headerText = `What is ${this.person.firstName}, the owner's, information?`;
    } else {
      this.personString = 'insured.person';
      this.person = this.controller.customer.application.primaryPolicy.insured.person;
      this.headerText = `What is ${this.person.firstName}, the insured's, information?`;
    }
  }

  private handleGeoChange(response): void {
    /*
        3 possible results from changing zip code

        1 - user changes zip, is prompted to change app, leaves app
                no further action here, this component is destroyed
                This function is not even invoked
        2 - user changes zip, is prompted to change app, choses to stay
                replace newly typed zip with original zip
                case: zipKept
        3 - user changes zip, does not require prompt to leave app
                just place new data into inputs
                case: newZip
        */
    switch (response.event) {
      case 'zipKept':
        // old zip was never replaced
        // just force it into child text input
        this.ssnForm.get('zip').patchValue(this.ssnForm.get('oldZip').value);
        break;
      case 'newZip':
        // push data from model zip into this component and child inputs
        this.ssnForm.get('oldZip').patchValue(response.newZip);
        this.ssnForm.get('oldState').patchValue(response.zipLookup['stateCode']);

        this.ssnForm.get('zip').patchValue(response.newZip);
        this.ssnForm.get('city').patchValue(response.zipLookup['city']);
        const state = response.zipLookup['stateCode'];
        this.ssnForm.get('state').patchValue(state);
        break;
    }
  }

  obfuscateSSN() {
    const formattedSSN = this.formatterService.format('ssn', this.ssnInput.val);
    const ssnFormControl = this.ssnForm.get('ssn');

    ssnFormControl.patchValue(formattedSSN);
    ssnFormControl.markAsTouched();

    if (!ssnFormControl.invalid) {
      this.ssnInput.val = this.obfuscationPattern;
    }
  }

  showSSN() {
    this.ssnInput.val = this.ssnForm.get('ssn').value;
  }

  flipPage(): void {
    this.ssnForm.updateValueAndValidity();

    if (this.ssnForm.valid === false) {
      return;
    }

    this.showLegalInfoPage.emit();
  }

  async validateAndContinue(): Promise<void> {
    this.ssnForm.updateValueAndValidity();

    if (!this.changesWereMade || this.ssnForm.valid === false) {
      return;
    }

    this.toggleLoadingSpinner.emit(true);

    this.person.firstName = this.ssnForm.get('firstName').value;
    this.person.lastName = this.ssnForm.get('lastName').value;
    this.person.middleName = this.ssnForm.get('middleName').value;
    if (
      this.controller.customer.application.primaryPolicy.isCoverpathPolicy() &&
      this.ssnForm.get('ssn').value !== this.oldSsnValue
    ) {
      const piiKey = await this.controller.setPiiByProperty(`${this.personString}.SSN`, this.ssnForm.get('ssn').value);
      this.person.SSN = piiKey;
    } else {
      this.person.SSN = this.ssnForm.get('ssn').value;
    }
    this.person.dob = new Date(this.ssnForm.get('dob').value);
    this.person.residentialAddress.street = this.ssnForm.get('street').value;
    this.person.residentialAddress.line1 = this.ssnForm.get('line1').value;
    this.person.residentialAddress.city = this.ssnForm.get('city').value;
    this.person.residentialAddress.state = this.ssnForm.get('state').value;
    this.person.residentialAddress.zip = this.ssnForm.get('zip').value;

    this.controller.publicSave();

    this.submitCorrection.emit();
  }

  private checkForChanges(): void {
    const personDOB = this.formatterService.formatUTCDate(this.person.dob as Date);

    if (
      this.ssnForm.get('firstName').value !== this.person.firstName ||
      this.ssnForm.get('middleName').value !== this.person.middleName ||
      this.ssnForm.get('lastName').value !== this.person.lastName ||
      this.ssnForm.get('ssn').value !== this.oldSsnValue ||
      this.ssnForm.get('dob').value !== personDOB ||
      this.ssnForm.get('street').value !== this.person.residentialAddress.street ||
      this.ssnForm.get('line1').value !== this.person.residentialAddress.line1 ||
      this.ssnForm.get('city').value !== this.person.residentialAddress.city ||
      this.ssnForm.get('state').value !== this.person.residentialAddress.state ||
      this.ssnForm.get('zip').value !== this.person.residentialAddress.zip
    ) {
      this.changesWereMade = true;
    } else {
      this.changesWereMade = false;
    }
  }
}
